import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ChevronDown, ChevronRight, Download, FileUp, Upload } from 'lucide-react'
import { ErrorApi } from '@/api/errores'
import { cx } from '@/lib/clases'
import { formatearFecha, formatearFechaHora } from '@/lib/fecha'
import { formatearMonto } from '@/lib/monto'
import { Banner } from '@/ui/Banner'
import { Boton } from '@/ui/Boton'
import { FilasEsqueleto } from '@/ui/Esqueleto'
import { EstadoError } from '@/ui/EstadoError'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { InsigniaDiscreta, InsigniaEstado } from '@/ui/InsigniaEstado'
import { Monto } from '@/ui/Numero'
import { PanelLateral } from '@/ui/PanelLateral'
import {
  DESTINOS,
  FORMATOS,
  TEXTO_RESULTADO,
  TIPOS_ANULACION,
  TITULO_TIPO,
  TONO_FILA,
  TONO_RESULTADO,
  claseDeArchivo,
  limitesDelMes,
  tamanoLegible,
  type ComprobanteExterno,
  type ComprobanteImportado,
  type DestinoXml,
  type FormatoPlanilla,
  type ImportacionHistorial,
  type ResultadoImportacion,
  type RevisionPlanilla,
} from '@/dominio/importacion/tipos'
import {
  descargarPlantilla,
  useComprobanteExterno,
  useComprobantesExternos,
  useDetalleImportacion,
  useHistorialImportaciones,
  useSubidas,
} from '@/dominio/importacion/useImportacion'
import { Dato, EncabezadoPantalla, Tarjeta } from './-pantalla'

/**
 * Importar comprobantes emitidos o recibidos fuera de la plataforma.
 *
 * Para el contribuyente que factura con otro proveedor —o en papel— y quiere
 * sus 606, 607 y 608 completos aquí. Tres cosas que la pantalla no puede dejar
 * de decir, porque son las que se malentienden:
 *
 *   **Lo importado es registro, no emisión.** No consume secuencia, no se
 *   transmite a DGII, no se entrega al comprador. Entra a los reportes marcado
 *   como externo
 *
 *   **Su estado ante DGII lo declara usted.** La plataforma valida el esquema y
 *   la firma del XML, pero no consulta a DGII si lo aceptó: eso exige
 *   autenticarse con el certificado y está pendiente
 *
 *   **La planilla es todo o nada.** Con una sola fila con error no entra
 *   ninguna: medio archivo importado deja un 607 que parece completo
 */
export interface BusquedaImportacion {
  pestana?: Pestana
  /** Un comprobante externo, desde el enlace de una inconsistencia del 607 o el 608. */
  comprobante?: string
}

type Pestana = 'xml' | 'planilla' | 'historial' | 'externos'

const PESTANAS: readonly { id: Pestana; titulo: string }[] = [
  { id: 'xml', titulo: 'Con XML' },
  { id: 'planilla', titulo: 'Sin XML · planilla' },
  { id: 'historial', titulo: 'Historial' },
  { id: 'externos', titulo: 'Comprobantes externos' },
]

export const Route = createFileRoute('/importacion')({
  validateSearch: (busqueda: Record<string, unknown>): BusquedaImportacion => ({
    pestana: PESTANAS.some((p) => p.id === busqueda.pestana) ? (busqueda.pestana as Pestana) : undefined,
    comprobante: typeof busqueda.comprobante === 'string' ? busqueda.comprobante : undefined,
  }),
  component: PantallaImportacion,
})

function PantallaImportacion() {
  const busqueda = Route.useSearch()
  const navegar = useNavigate({ from: '/importacion' })
  const pestana: Pestana = busqueda.pestana ?? (busqueda.comprobante ? 'externos' : 'xml')

  const cambiar = (id: Pestana) => void navegar({ search: { pestana: id } })

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoPantalla
        titulo="Importar comprobantes"
        descripcion={
          <>
            Lo que emitió con otro proveedor o en papel, y lo que le facturaron por fuera, para que su 606, su 607 y su 608
            salgan completos. <strong className="font-medium text-gris-900">Lo importado no se transmite ni se entrega</strong>: se
            declara, y su estado ante la DGII queda como declarado por usted.
          </>
        }
      />

      <div role="tablist" aria-label="Formas de importar" className="flex flex-wrap gap-1 border-b border-gris-300">
        {PESTANAS.map((p) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={pestana === p.id}
            onClick={() => cambiar(p.id)}
            className={cx(
              '-mb-px border-b-2 px-3 py-2 text-cuerpo',
              pestana === p.id ? 'border-marca font-medium text-marca' : 'border-transparent text-gris-700 hover:text-gris-900',
            )}
          >
            {p.titulo}
          </button>
        ))}
      </div>

      {pestana === 'xml' ? <PanelXml /> : null}
      {pestana === 'planilla' ? <PanelPlanilla /> : null}
      {pestana === 'historial' ? <PanelHistorial /> : null}
      {pestana === 'externos' ? <PanelExternos /> : null}

      <FichaExterno
        id={busqueda.comprobante ?? null}
        onCerrar={() => void navegar({ search: { pestana } })}
      />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Zona de arrastre                                                    */
/* ------------------------------------------------------------------ */

function ZonaArrastre({
  aceptar,
  multiple,
  deshabilitada,
  onArchivos,
  children,
}: {
  aceptar: string
  multiple: boolean
  deshabilitada?: boolean
  onArchivos: (archivos: File[]) => void
  children: ReactNode
}) {
  const entrada = useRef<HTMLInputElement>(null)
  const [encima, setEncima] = useState(false)

  const soltar = (evento: DragEvent<HTMLDivElement>) => {
    evento.preventDefault()
    setEncima(false)
    if (deshabilitada) return
    const archivos = Array.from(evento.dataTransfer.files)
    if (archivos.length > 0) onArchivos(multiple ? archivos : archivos.slice(0, 1))
  }

  return (
    <div
      onDragOver={(evento) => {
        evento.preventDefault()
        if (!deshabilitada) setEncima(true)
      }}
      onDragLeave={() => setEncima(false)}
      onDrop={soltar}
      className={cx(
        'flex flex-col items-center justify-center gap-2 rounded-md border border-dashed px-4 py-8 text-center',
        encima ? 'border-marca bg-marca-tenue' : 'border-gris-500 bg-gris-50',
        deshabilitada && 'opacity-60',
      )}
    >
      <Upload aria-hidden="true" className="size-5 text-gris-600" />
      <div className="text-cuerpo text-gris-700">{children}</div>
      <Boton
        variante="secundario"
        tamano="menor"
        icono={<FileUp />}
        disabled={deshabilitada}
        onClick={() => entrada.current?.click()}
      >
        Elegir {multiple ? 'archivos' : 'archivo'}
      </Boton>
      <input
        ref={entrada}
        type="file"
        accept={aceptar}
        multiple={multiple}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(evento) => {
          const archivos = Array.from(evento.target.files ?? [])
          evento.target.value = ''
          if (archivos.length > 0) onArchivos(archivos)
        }}
      />
    </div>
  )
}

function BarraProgreso({ porcentaje, etiqueta }: { porcentaje: number; etiqueta: string }) {
  const acotado = Math.min(100, Math.max(0, porcentaje))
  return (
    <span
      role="progressbar"
      aria-valuenow={acotado}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={etiqueta}
      className="block h-1 w-full overflow-hidden rounded-md bg-gris-200"
    >
      <span className="block h-full bg-marca transition-[width]" style={{ width: `${acotado}%` }} />
    </span>
  )
}

function mensajeDeError(error: unknown): string {
  if (error instanceof ErrorApi) {
    if (error.clase === 'permiso') return 'Importar exige poder ver los reportes fiscales o administrar el contribuyente.'
    return error.message
  }
  return 'No se pudo completar la subida.'
}

/* ------------------------------------------------------------------ */
/* Con XML                                                             */
/* ------------------------------------------------------------------ */

interface Subida {
  id: number
  archivo: File
  destino: DestinoXml
  estado: 'en-cola' | 'subiendo' | 'listo' | 'fallo'
  progreso: number
  resultado?: ResultadoImportacion
  error?: string
}

/**
 * Los XML se suben **uno por archivo y en fila**, no en paralelo: cada archivo
 * deja su línea en el historial, y en fila la barra de cada uno dice la verdad.
 * Un ZIP cuenta como un archivo.
 */
function PanelXml() {
  const [destino, setDestino] = useState<DestinoXml>('ventas')
  const [subidas, setSubidas] = useState<Subida[]>([])
  const [descartados, setDescartados] = useState<string[]>([])
  const siguiente = useRef(1)
  const { importarXml } = useSubidas()

  const actualizar = useCallback((id: number, cambio: Partial<Subida>) => {
    setSubidas((lista) => lista.map((s) => (s.id === id ? { ...s, ...cambio } : s)))
  }, [])

  const agregar = (archivos: File[]) => {
    const validos = archivos.filter((a) => claseDeArchivo(a.name) === 'xml')
    setDescartados(archivos.filter((a) => claseDeArchivo(a.name) !== 'xml').map((a) => a.name))
    setSubidas((lista) => [
      ...lista,
      ...validos.map((archivo) => ({
        id: siguiente.current++,
        archivo,
        destino,
        estado: 'en-cola' as const,
        progreso: 0,
      })),
    ])
  }

  const enCurso = subidas.some((s) => s.estado === 'subiendo')
  const proxima = subidas.find((s) => s.estado === 'en-cola')

  useEffect(() => {
    if (enCurso || proxima === undefined) return
    actualizar(proxima.id, { estado: 'subiendo' })
    importarXml(proxima.destino, proxima.archivo, (progreso) => actualizar(proxima.id, { progreso }))
      .then((resultado) => actualizar(proxima.id, { estado: 'listo', progreso: 100, resultado }))
      .catch((error: unknown) => actualizar(proxima.id, { estado: 'fallo', error: mensajeDeError(error) }))
  }, [enCurso, proxima, importarXml, actualizar])

  const totales = useMemo(() => {
    const listos = subidas.flatMap((s) => (s.resultado ? [s.resultado] : []))
    return {
      importados: listos.reduce((n, r) => n + r.importados, 0),
      duplicados: listos.reduce((n, r) => n + r.duplicados, 0),
      rechazados: listos.reduce((n, r) => n + r.rechazados, 0),
    }
  }, [subidas])

  return (
    <div className="flex flex-col gap-4">
      <Tarjeta titulo="Qué va a importar">
        <fieldset className="grid gap-2 md:grid-cols-2">
          <legend className="sr-only">Destino de los XML</legend>
          {DESTINOS.map((d) => (
            <label
              key={d.destino}
              className={cx(
                'flex cursor-pointer gap-2 rounded-md border p-3',
                destino === d.destino ? 'border-marca bg-marca-tenue' : 'border-gris-300 hover:bg-gris-50',
              )}
            >
              <input
                type="radio"
                name="destino"
                value={d.destino}
                checked={destino === d.destino}
                onChange={() => setDestino(d.destino)}
                className="mt-1"
              />
              <span className="flex flex-col gap-1">
                <span className="text-cuerpo font-medium text-gris-900">{d.titulo}</span>
                <span className="text-etiqueta text-gris-600">{d.descripcion}</span>
              </span>
            </label>
          ))}
        </fieldset>
      </Tarjeta>

      <ZonaArrastre aceptar=".xml,.zip" multiple onArchivos={agregar}>
        Arrastre aquí los XML de los e-CF, o un ZIP con muchos. Cada uno se valida contra el esquema de DGII y se verifica su
        firma antes de guardarlo tal como llegó.
      </ZonaArrastre>

      {descartados.length > 0 ? (
        <Banner tono="ambar" titulo="Algunos archivos no son XML ni ZIP" onCerrar={() => setDescartados([])}>
          {descartados.join(', ')}. Las planillas CSV o Excel van en la pestaña «Sin XML · planilla».
        </Banner>
      ) : null}

      {subidas.length > 0 ? (
        <Tarjeta
          titulo="Archivos"
          acciones={
            <span className="text-etiqueta text-gris-600">
              <span className="numero">{formatearMonto(totales.importados, { decimales: 0 })}</span> importados ·{' '}
              <span className="numero">{formatearMonto(totales.duplicados, { decimales: 0 })}</span> duplicados ·{' '}
              <span className="numero">{formatearMonto(totales.rechazados, { decimales: 0 })}</span> rechazados
            </span>
          }
          relleno={false}
        >
          <ul className="divide-y divide-gris-200">
            {subidas.map((s) => (
              <FilaSubida key={s.id} subida={s} />
            ))}
          </ul>
        </Tarjeta>
      ) : null}
    </div>
  )
}

function FilaSubida({ subida }: { subida: Subida }) {
  const [abierta, setAbierta] = useState(false)
  const r = subida.resultado

  return (
    <li className="flex flex-col gap-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="min-w-0 flex-1 truncate text-cuerpo text-gris-900" title={subida.archivo.name}>
          {subida.archivo.name}
        </span>
        <span className="text-etiqueta text-gris-600">
          {subida.destino === 'ventas' ? 'ventas' : 'compras'} · {tamanoLegible(subida.archivo.size)}
        </span>
        {subida.estado === 'en-cola' ? <InsigniaDiscreta>en cola</InsigniaDiscreta> : null}
        {subida.estado === 'subiendo' ? (
          <InsigniaEstado tono="azul">{subida.progreso < 100 ? `subiendo ${subida.progreso} %` : 'validando'}</InsigniaEstado>
        ) : null}
        {subida.estado === 'fallo' ? <InsigniaEstado tono="rojo">no se importó</InsigniaEstado> : null}
        {r ? (
          <span className="text-etiqueta text-gris-700">
            <span className="numero">{r.importados}</span> importados · <span className="numero">{r.duplicados}</span>{' '}
            duplicados · <span className={cx('numero', r.rechazados > 0 && 'text-rojo')}>{r.rechazados}</span> rechazados
          </span>
        ) : null}
        {r && r.comprobantes.length > 0 ? (
          <Boton
            variante="discreto"
            tamano="menor"
            icono={abierta ? <ChevronDown /> : <ChevronRight />}
            aria-expanded={abierta}
            onClick={() => setAbierta(!abierta)}
          >
            Detalle
          </Boton>
        ) : null}
      </div>

      {subida.estado === 'subiendo' ? <BarraProgreso porcentaje={subida.progreso} etiqueta={`Subida de ${subida.archivo.name}`} /> : null}
      {subida.error ? <p className="text-etiqueta text-rojo">{subida.error}</p> : null}
      {r?.avisos.map((aviso) => (
        <p key={aviso} className="text-etiqueta text-gris-600">
          {aviso}
        </p>
      ))}
      {r && abierta ? <ListaComprobantes comprobantes={r.comprobantes} /> : null}
    </li>
  )
}

/** Lo que pasó con cada comprobante: primero lo que no entró, que es lo que pide acción. */
function ListaComprobantes({ comprobantes }: { comprobantes: readonly ComprobanteImportado[] }) {
  const ordenados = useMemo(() => {
    const peso = { rechazado: 0, duplicado: 1, importado: 2 } as const
    return [...comprobantes].sort((a, b) => peso[a.resultado] - peso[b.resultado])
  }, [comprobantes])

  return (
    <div className="max-h-[360px] overflow-auto rounded-md border border-gris-200">
      <table className="w-full border-collapse text-cuerpo">
        <thead className="sticky top-0 bg-gris-50">
          <tr className="border-b border-gris-300 text-left text-etiqueta text-gris-600">
            <th scope="col" className="px-2 py-1 font-semibold">Archivo</th>
            <th scope="col" className="px-2 py-1 font-semibold">NCF</th>
            <th scope="col" className="px-2 py-1 font-semibold">Resultado</th>
            <th scope="col" className="px-2 py-1 font-semibold">Motivo y avisos</th>
          </tr>
        </thead>
        <tbody>
          {ordenados.map((c, i) => (
            <tr key={`${c.archivo}-${c.linea ?? ''}-${i}`} className="border-b border-gris-200 align-top">
              <td className="max-w-[220px] truncate px-2 py-1 text-gris-700" title={c.archivo}>
                {c.linea !== null ? `línea ${c.linea}` : c.archivo}
              </td>
              <td className="mono px-2 py-1 text-gris-900">{c.ncf ?? '—'}</td>
              <td className="px-2 py-1">
                <InsigniaEstado tono={TONO_RESULTADO[c.resultado]}>{TEXTO_RESULTADO[c.resultado]}</InsigniaEstado>
              </td>
              <td className="px-2 py-1 text-etiqueta text-gris-700">
                {c.motivo ? <p>{c.motivo}</p> : null}
                {c.avisos.map((a) => (
                  <p key={a} className="text-ambar">
                    {a}
                  </p>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Sin XML: planilla                                                   */
/* ------------------------------------------------------------------ */

type FasePlanilla =
  | { fase: 'vacia' }
  | { fase: 'revisando'; archivo: File; progreso: number }
  | { fase: 'revisada'; archivo: File; revision: RevisionPlanilla }
  | { fase: 'importando'; archivo: File; revision: RevisionPlanilla; progreso: number }
  | { fase: 'importada'; archivo: File; resultado: ResultadoImportacion }

function PanelPlanilla() {
  const [formato, setFormato] = useState<FormatoPlanilla>('607')
  const [estado, setEstado] = useState<FasePlanilla>({ fase: 'vacia' })
  const [error, setError] = useState<string | null>(null)
  const { revisarPlanilla, importarPlanilla } = useSubidas()

  const revisar = (archivos: File[]) => {
    const archivo = archivos[0]
    if (archivo === undefined) return
    setError(null)

    if (claseDeArchivo(archivo.name) !== 'planilla') {
      setError(`«${archivo.name}» no es un CSV ni un Excel (.xlsx). Los XML van en la pestaña «Con XML».`)
      return
    }

    setEstado({ fase: 'revisando', archivo, progreso: 0 })
    revisarPlanilla(formato, archivo, (progreso) => setEstado({ fase: 'revisando', archivo, progreso }))
      .then((revision) => setEstado({ fase: 'revisada', archivo, revision }))
      .catch((e: unknown) => {
        setEstado({ fase: 'vacia' })
        setError(mensajeDeError(e))
      })
  }

  const importar = () => {
    if (estado.fase !== 'revisada') return
    const { archivo, revision } = estado
    setError(null)
    setEstado({ fase: 'importando', archivo, revision, progreso: 0 })
    importarPlanilla(formato, archivo, (progreso) => setEstado({ fase: 'importando', archivo, revision, progreso }))
      .then((respuesta) =>
        respuesta.importada && respuesta.importacion
          ? setEstado({ fase: 'importada', archivo, resultado: respuesta.importacion })
          : setEstado({ fase: 'revisada', archivo, revision: respuesta.revision }),
      )
      .catch((e: unknown) => {
        setEstado({ fase: 'revisada', archivo, revision })
        setError(mensajeDeError(e))
      })
  }

  const ocupado = estado.fase === 'revisando' || estado.fase === 'importando'

  return (
    <div className="flex flex-col gap-4">
      <Tarjeta
        titulo="Formato de la planilla"
        acciones={
          <Boton
            variante="secundario"
            tamano="menor"
            icono={<Download />}
            onClick={() => void descargarPlantilla(formato).catch((e: unknown) => setError(mensajeDeError(e)))}
          >
            Plantilla del {formato}
          </Boton>
        }
      >
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Formato">
          {FORMATOS.map((f) => (
            <button
              key={f.formato}
              type="button"
              role="radio"
              aria-checked={formato === f.formato}
              disabled={ocupado}
              onClick={() => {
                setFormato(f.formato)
                setEstado({ fase: 'vacia' })
              }}
              className={cx(
                'flex flex-col items-start gap-1 rounded-md border px-3 py-2 text-left',
                formato === f.formato ? 'border-marca bg-marca-tenue' : 'border-gris-300 hover:bg-gris-50',
              )}
            >
              <span className="text-cuerpo font-medium text-gris-900">{f.titulo}</span>
              <span className="text-etiqueta text-gris-600">{f.descripcion}</span>
            </button>
          ))}
        </div>
        <p className="mt-3 text-etiqueta text-gris-600">
          Las columnas son las del formato de DGII, en su orden; se reconocen por su encabezado. Fechas en AAAAMMDD o
          día/mes/año, importes con punto decimal. Las compras entran como carga manual; las ventas y los anulados, como
          comprobantes externos sin XML.
        </p>
      </Tarjeta>

      {error ? (
        <Banner tono="rojo" titulo="No se pudo" onCerrar={() => setError(null)}>
          {error}
        </Banner>
      ) : null}

      {estado.fase === 'vacia' || estado.fase === 'revisando' ? (
        <ZonaArrastre aceptar=".csv,.xlsx,.txt" multiple={false} deshabilitada={ocupado} onArchivos={revisar}>
          Arrastre aquí la planilla del {formato}, en CSV o Excel (.xlsx). Primero se revisa fila por fila; nada se importa
          hasta que usted confirme.
          {estado.fase === 'revisando' ? (
            <span className="mt-2 block w-64">
              <BarraProgreso porcentaje={estado.progreso} etiqueta="Revisión de la planilla" />
            </span>
          ) : null}
        </ZonaArrastre>
      ) : null}

      {estado.fase === 'revisada' || estado.fase === 'importando' ? (
        <VistaPrevia
          archivo={estado.archivo}
          revision={estado.revision}
          importando={estado.fase === 'importando'}
          progreso={estado.fase === 'importando' ? estado.progreso : 0}
          onImportar={importar}
          onCancelar={() => setEstado({ fase: 'vacia' })}
        />
      ) : null}

      {estado.fase === 'importada' ? (
        <Tarjeta
          titulo={`Importada: ${estado.archivo.name}`}
          acciones={
            <Boton variante="secundario" tamano="menor" onClick={() => setEstado({ fase: 'vacia' })}>
              Importar otra
            </Boton>
          }
        >
          <Banner tono="verde" titulo={`${estado.resultado.importados} comprobante(s) importados`}>
            {estado.resultado.duplicados > 0
              ? `${estado.resultado.duplicados} ya estaban y no se repitieron. `
              : null}
            Ya cuentan en los reportes del período, marcados como externos.{' '}
            <Link to="/reportes" className="text-marca underline">
              Ir a reportes
            </Link>
          </Banner>
          {estado.resultado.comprobantes.some((c) => c.resultado !== 'importado' || c.avisos.length > 0) ? (
            <div className="mt-3">
              <ListaComprobantes
                comprobantes={estado.resultado.comprobantes.filter((c) => c.resultado !== 'importado' || c.avisos.length > 0)}
              />
            </div>
          ) : null}
        </Tarjeta>
      ) : null}
    </div>
  )
}

/**
 * La vista previa de la planilla.
 *
 * No usa `PrevisualizacionImportacion` de `ui/` y es a propósito: ese
 * componente cuenta listas, errores y duplicadas sobre las filas que recibe, y
 * aquí el servidor manda solo las que tienen algo que decir —una planilla del
 * 607 puede traer 65,000 filas—. Los contadores tienen que ser los de la
 * planilla entera, que vienen del servidor.
 */
function VistaPrevia({
  archivo,
  revision,
  importando,
  progreso,
  onImportar,
  onCancelar,
}: {
  archivo: File
  revision: RevisionPlanilla
  importando: boolean
  progreso: number
  onImportar: () => void
  onCancelar: () => void
}) {
  // Las columnas del formato que traen algo en las filas que se enseñan: las 23
  // del 606 no caben, y una columna vacía en todas no dice nada.
  const columnas = revision.columnas.filter((c) => revision.filas.some((f) => f.valores[c.clave] !== undefined)).slice(0, 9)
  const omitidas = revision.totalFilas - revision.filas.length

  return (
    <section className="flex flex-col rounded-md border border-gris-300 bg-white">
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gris-300 px-3 py-2">
        <h2 className="text-seccion text-gris-900">Revisión de {archivo.name}</h2>
        <p className="text-etiqueta text-gris-600">
          Planilla del {revision.formato} · {formatearMonto(revision.totalFilas, { decimales: 0 })} filas
        </p>
      </header>

      {revision.erroresDeArchivo.length > 0 ? (
        <Banner tono="rojo" titulo="El archivo no se puede leer como planilla del formato" className="m-3">
          <ul className="list-disc pl-4">
            {revision.erroresDeArchivo.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </Banner>
      ) : null}

      {revision.avisosDeArchivo.length > 0 ? (
        <Banner tono="ambar" titulo="A tener en cuenta" className="m-3">
          <ul className="list-disc pl-4">
            {revision.avisosDeArchivo.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </Banner>
      ) : null}

      {revision.filas.length > 0 ? (
        <div className="max-h-[420px] overflow-auto" data-densidad="compacta">
          <table className="w-full border-collapse text-cuerpo">
            <thead className="sticky top-0 z-[1] bg-gris-50">
              <tr className="border-b border-gris-300">
                <th scope="col" className="h-[var(--alto-fila)] w-14 px-2 text-right text-etiqueta font-semibold text-gris-600">
                  Línea
                </th>
                {columnas.map((c) => (
                  <th
                    key={c.clave}
                    scope="col"
                    title={c.ayuda}
                    className="h-[var(--alto-fila)] whitespace-nowrap px-2 text-left text-etiqueta font-semibold text-gris-600"
                  >
                    {c.columnaDgii !== null ? `${c.columnaDgii}. ` : ''}
                    {c.encabezado}
                  </th>
                ))}
                <th scope="col" className="h-[var(--alto-fila)] px-2 text-left text-etiqueta font-semibold text-gris-600">
                  Resultado
                </th>
              </tr>
            </thead>
            <tbody>
              {revision.filas.map((f) => (
                <tr key={f.linea} className="border-b border-gris-200 align-top">
                  <td className="numero px-2 py-1 text-gris-600">{f.linea}</td>
                  {columnas.map((c) => (
                    <td key={c.clave} className="max-w-[180px] truncate px-2 py-1" title={f.valores[c.clave]}>
                      {f.valores[c.clave] ?? ''}
                    </td>
                  ))}
                  <td className="min-w-[260px] px-2 py-1">
                    <InsigniaEstado tono={TONO_FILA[f.resultado]}>{f.resultado === 'ok' ? 'lista' : f.resultado}</InsigniaEstado>
                    {f.errores.map((e) => (
                      <p key={e} className="text-etiqueta text-rojo">
                        {e}
                      </p>
                    ))}
                    {f.avisos.map((a) => (
                      <p key={a} className="text-etiqueta text-gris-700">
                        {a}
                      </p>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {omitidas > 0 ? (
        <p className="border-t border-gris-200 px-3 py-2 text-etiqueta text-gris-600">
          Se enseñan las filas con errores, avisos o repetidas, y algunas limpias. Las otras{' '}
          {formatearMonto(omitidas, { decimales: 0 })} están listas.
        </p>
      ) : null}

      {importando ? (
        <div className="px-3 py-2">
          <BarraProgreso porcentaje={progreso} etiqueta="Importación de la planilla" />
        </div>
      ) : null}

      <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-gris-300 bg-gris-50 px-3 py-2">
        <p className="text-cuerpo text-gris-700">
          <span className="numero">{formatearMonto(revision.listas, { decimales: 0 })}</span> listas ·{' '}
          <span className={cx('numero', revision.conError > 0 && 'text-rojo')}>
            {formatearMonto(revision.conError, { decimales: 0 })}
          </span>{' '}
          con error · <span className="numero">{formatearMonto(revision.duplicadas, { decimales: 0 })}</span> repetidas
        </p>
        <div className="flex items-center gap-2">
          {!revision.sePuedeImportar && revision.conError > 0 ? (
            <span className="text-etiqueta text-gris-600">Todo o nada: corrija las filas con error y vuelva a subirla.</span>
          ) : null}
          <Boton variante="primario" onClick={onImportar} disabled={importando || !revision.sePuedeImportar}>
            Importar {formatearMonto(revision.listas, { decimales: 0 })}
          </Boton>
          <Boton variante="discreto" onClick={onCancelar} disabled={importando}>
            Cancelar
          </Boton>
        </div>
      </footer>
    </section>
  )
}

/* ------------------------------------------------------------------ */
/* Historial                                                           */
/* ------------------------------------------------------------------ */

function PanelHistorial() {
  const [pagina, setPagina] = useState(1)
  const [abierta, setAbierta] = useState<ImportacionHistorial | null>(null)
  const consulta = useHistorialImportaciones(pagina)

  if (consulta.isPending) return <FilasEsqueleto filas={6} columnas={6} />

  if (consulta.isError) {
    return (
      <EstadoError
        titulo="No se pudo cargar el historial."
        contexto={mensajeDeError(consulta.error)}
        onReintentar={() => void consulta.refetch()}
      />
    )
  }

  const { filas, total, tamano } = consulta.data

  if (filas.length === 0) {
    return <EstadoVacio variante="primera-vez" mensaje="Todavía no se ha importado nada." />
  }

  const paginas = Math.max(1, Math.ceil(total / tamano))

  return (
    <Tarjeta titulo="Historial de importaciones" relleno={false}>
      <div className="overflow-auto">
        <table className="w-full border-collapse text-cuerpo">
          <thead className="bg-gris-50">
            <tr className="border-b border-gris-300 text-left text-etiqueta text-gris-600">
              <th scope="col" className="px-3 py-2 font-semibold">Cuándo</th>
              <th scope="col" className="px-3 py-2 font-semibold">Qué</th>
              <th scope="col" className="px-3 py-2 font-semibold">Archivo</th>
              <th scope="col" className="px-3 py-2 font-semibold">Quién</th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">Importados</th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">Duplicados</th>
              <th scope="col" className="px-3 py-2 text-right font-semibold">Rechazados</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((h) => (
              <tr
                key={h.id}
                tabIndex={0}
                onClick={() => setAbierta(h)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setAbierta(h)
                }}
                className="cursor-pointer border-b border-gris-200 hover:bg-gris-50"
              >
                <td className="numero whitespace-nowrap px-3 py-2 text-gris-700">{formatearFechaHora(h.importadaEl)}</td>
                <td className="whitespace-nowrap px-3 py-2">{TITULO_TIPO[h.tipo]}</td>
                <td className="max-w-[260px] truncate px-3 py-2" title={h.archivo}>
                  {h.archivo}
                </td>
                <td className="px-3 py-2 text-gris-700">
                  {h.porClaveDeIntegracion ? 'clave de integración' : (h.usuario ?? '—')}
                </td>
                <td className="numero px-3 py-2 text-right">{h.importados}</td>
                <td className="numero px-3 py-2 text-right">{h.duplicados}</td>
                <td className={cx('numero px-3 py-2 text-right', h.rechazados > 0 && 'text-rojo')}>{h.rechazados}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {paginas > 1 ? (
        <div className="flex items-center justify-end gap-2 border-t border-gris-200 px-3 py-2 text-etiqueta text-gris-600">
          <Boton variante="discreto" tamano="menor" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>
            Anterior
          </Boton>
          <span>
            Página {pagina} de {paginas}
          </span>
          <Boton variante="discreto" tamano="menor" disabled={pagina >= paginas} onClick={() => setPagina(pagina + 1)}>
            Siguiente
          </Boton>
        </div>
      ) : null}

      <DetalleDeImportacion importacion={abierta} onCerrar={() => setAbierta(null)} />
    </Tarjeta>
  )
}

function DetalleDeImportacion({
  importacion,
  onCerrar,
}: {
  importacion: ImportacionHistorial | null
  onCerrar: () => void
}) {
  const detalle = useDetalleImportacion(importacion?.id ?? null)

  return (
    <PanelLateral
      abierto={importacion !== null}
      onCerrar={onCerrar}
      titulo={importacion ? TITULO_TIPO[importacion.tipo] : 'Importación'}
      subtitulo={importacion?.archivo}
      ancho="ancho"
    >
      {importacion ? (
        <div className="flex flex-col gap-4">
          <dl>
            <Dato etiqueta="Cuándo">{formatearFechaHora(importacion.importadaEl)}</Dato>
            <Dato etiqueta="Quién">
              {importacion.porClaveDeIntegracion ? 'clave de integración' : (importacion.usuario ?? '—')}
            </Dato>
            <Dato etiqueta="Tamaño">{tamanoLegible(importacion.bytes)}</Dato>
            <Dato etiqueta="Huella SHA-256">
              <span className="mono break-all text-etiqueta">{importacion.hash}</span>
            </Dato>
            <Dato etiqueta="Resultado">
              {importacion.importados} importados · {importacion.duplicados} duplicados · {importacion.rechazados} rechazados
            </Dato>
          </dl>
          {detalle.isPending ? <FilasEsqueleto filas={4} columnas={4} /> : null}
          {detalle.isError ? <EstadoError titulo="No se pudo cargar el detalle." onReintentar={() => void detalle.refetch()} /> : null}
          {detalle.data ? <ListaComprobantes comprobantes={detalle.data.comprobantes} /> : null}
        </div>
      ) : null}
    </PanelLateral>
  )
}

/* ------------------------------------------------------------------ */
/* Comprobantes externos                                               */
/* ------------------------------------------------------------------ */

function mesActual(): string {
  const hoy = new Date()
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`
}

function PanelExternos() {
  const [mes, setMes] = useState(mesActual)
  const [pagina, setPagina] = useState(1)
  const navegar = useNavigate({ from: '/importacion' })
  const { desde, hasta } = limitesDelMes(mes)
  const consulta = useComprobantesExternos(desde, hasta, pagina)

  return (
    <Tarjeta
      titulo="Comprobantes externos del mes"
      acciones={
        <label className="flex items-center gap-2 text-etiqueta text-gris-600">
          Mes
          <input
            type="month"
            value={mes}
            onChange={(e) => {
              if (e.target.value !== '') {
                setMes(e.target.value)
                setPagina(1)
              }
            }}
            className="h-[var(--alto-control-menor)] rounded-md border border-gris-300 px-2 text-cuerpo"
          />
        </label>
      }
      relleno={false}
    >
      <p className="border-b border-gris-200 px-4 py-2 text-etiqueta text-gris-600">
        Lo emitido fuera de la plataforma va al 607; las anulaciones de papel, al 608. Ninguno se transmitió desde aquí, y
        su estado ante la DGII es el que usted declaró.
      </p>

      {consulta.isPending ? <FilasEsqueleto filas={5} columnas={6} /> : null}
      {consulta.isError ? (
        <EstadoError
          titulo="No se pudieron cargar los comprobantes externos."
          contexto={mensajeDeError(consulta.error)}
          onReintentar={() => void consulta.refetch()}
        />
      ) : null}
      {consulta.data && consulta.data.filas.length === 0 ? (
        <EstadoVacio variante="sin-resultados" mensaje="No hay comprobantes externos en este mes." />
      ) : null}

      {consulta.data && consulta.data.filas.length > 0 ? (
        <div className="overflow-auto">
          <table className="w-full border-collapse text-cuerpo">
            <thead className="bg-gris-50">
              <tr className="border-b border-gris-300 text-left text-etiqueta text-gris-600">
                <th scope="col" className="px-3 py-2 font-semibold">Fecha</th>
                <th scope="col" className="px-3 py-2 font-semibold">NCF</th>
                <th scope="col" className="px-3 py-2 font-semibold">Va al</th>
                <th scope="col" className="px-3 py-2 font-semibold">Comprador</th>
                <th scope="col" className="px-3 py-2 text-right font-semibold">Total</th>
                <th scope="col" className="px-3 py-2 font-semibold">Estado</th>
              </tr>
            </thead>
            <tbody>
              {consulta.data.filas.map((e) => (
                <tr
                  key={e.id}
                  tabIndex={0}
                  onClick={() => void navegar({ search: { pestana: 'externos', comprobante: e.id } })}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') void navegar({ search: { pestana: 'externos', comprobante: e.id } })
                  }}
                  className="cursor-pointer border-b border-gris-200 hover:bg-gris-50"
                >
                  <td className="numero whitespace-nowrap px-3 py-2">{formatearFecha(e.fechaAnulacion ?? e.fechaEmision)}</td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <span className="mono">{e.ncf}</span>{' '}
                    <InsigniaDiscreta>{e.electronico ? (e.conXml ? 'e-CF con XML' : 'e-CF sin XML') : 'papel'}</InsigniaDiscreta>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">{e.clase === 'emitido' ? '607' : '608'}</td>
                  <td className="max-w-[240px] truncate px-3 py-2" title={e.razonSocialComprador ?? e.comprador ?? ''}>
                    {e.clase === 'anulacion'
                      ? (TIPOS_ANULACION[e.tipoAnulacion ?? 0] ?? 'anulación')
                      : (e.razonSocialComprador ?? e.comprador ?? '—')}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {e.clase === 'emitido' ? <Monto valor={e.total} /> : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <InsigniaEstado tono="neutral">externo · declarado por usted</InsigniaEstado>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {consulta.data && consulta.data.total > consulta.data.tamano ? (
        <div className="flex items-center justify-end gap-2 border-t border-gris-200 px-3 py-2 text-etiqueta text-gris-600">
          <Boton variante="discreto" tamano="menor" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>
            Anterior
          </Boton>
          <span>Página {pagina}</span>
          <Boton
            variante="discreto"
            tamano="menor"
            disabled={pagina * consulta.data.tamano >= consulta.data.total}
            onClick={() => setPagina(pagina + 1)}
          >
            Siguiente
          </Boton>
        </div>
      ) : null}
    </Tarjeta>
  )
}

/** La ficha de un comprobante externo: el destino de los enlaces del 607 y el 608. */
function FichaExterno({ id, onCerrar }: { id: string | null; onCerrar: () => void }) {
  const consulta = useComprobanteExterno(id)
  const e: ComprobanteExterno | undefined = consulta.data

  return (
    <PanelLateral abierto={id !== null} onCerrar={onCerrar} titulo="Comprobante externo" subtitulo={e?.ncf}>
      {consulta.isPending && id !== null ? <FilasEsqueleto filas={6} columnas={2} /> : null}
      {consulta.isError ? (
        <EstadoError titulo="No se pudo cargar el comprobante." contexto={mensajeDeError(consulta.error)} />
      ) : null}
      {e ? (
        <div className="flex flex-col gap-4">
          <Banner tono="azul" titulo="Emitido fuera de la plataforma">
            Entró por el importador. No se transmitió ni se entregó desde aquí, y su estado ante la DGII lo declaró usted.
          </Banner>
          <dl>
            <Dato etiqueta="NCF">
              <span className="mono">{e.ncf}</span>
            </Dato>
            <Dato etiqueta="Va al">{e.clase === 'emitido' ? '607 · ventas' : '608 · anulados'}</Dato>
            <Dato etiqueta="Fecha del comprobante">{formatearFecha(e.fechaEmision)}</Dato>
            {e.fechaAnulacion ? <Dato etiqueta="Fecha de anulación">{formatearFecha(e.fechaAnulacion)}</Dato> : null}
            {e.tipoAnulacion !== null ? (
              <Dato etiqueta="Tipo de anulación">
                {e.tipoAnulacion}. {TIPOS_ANULACION[e.tipoAnulacion] ?? ''}
              </Dato>
            ) : null}
            {e.clase === 'emitido' ? (
              <>
                <Dato etiqueta="Comprador">{e.razonSocialComprador ?? e.comprador ?? '—'}</Dato>
                <Dato etiqueta="Identificación">{e.comprador ?? '—'}</Dato>
                {e.ncfModificado ? <Dato etiqueta="Modifica a">{e.ncfModificado}</Dato> : null}
                <Dato etiqueta="Monto facturado">
                  <Monto valor={e.montoFacturado} />
                </Dato>
                <Dato etiqueta="ITBIS">
                  <Monto valor={e.itbis} />
                </Dato>
                <Dato etiqueta="Total">
                  <Monto valor={e.total} mostrarMoneda />
                </Dato>
              </>
            ) : null}
            <Dato etiqueta="XML">{e.conXml ? 'conservado en el almacén, firma verificada' : 'sin XML'}</Dato>
            <Dato etiqueta="Importado">{formatearFechaHora(e.importadoEl)}</Dato>
          </dl>
        </div>
      ) : null}
    </PanelLateral>
  )
}
